import {
  Improvements,
  ManufacturedHomeFeatures,
  ManufacturedHomeFoundationDisplay,
  getFoundationDisplay,
  isDefined,
} from '@aloft/toolkit-shared';

import { PropertyGridSection } from '../../../components/PropertyGrid/columnSections/PropertyGridSection.tsx';
import {
  getAloftAreaJSONPropertyGridField,
  getNumberPropertyGridField,
} from '../../../components/PropertyGrid/fields/utils.tsx';
import { PropertyGridSectionProps } from '../../../components/PropertyGrid/types.ts';
import { selectSalesComparisonApproach } from '../../../redux/features/selectors/orderSelectors.ts';
import {
  selectOrderHasExternalDepreciation,
  selectOrderHasFunctionalDepreciation,
} from '../../../redux/features/selectors/selectOrderDepreciation.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { ADJUSTMENT_LABELS } from '../../adjustments/internal/PairedCostSettings/internal/consts.ts';
import { ImprovementField, improvementsFieldName } from './consts.ts';

export const AdjustmentsGridMainDwellingSection = (props: PropertyGridSectionProps) => {
  const orderHasFunctionalDepreciation = useAppSelector(selectOrderHasFunctionalDepreciation);
  const orderHasExternalDepreciation = useAppSelector(selectOrderHasExternalDepreciation);
  const isUnitRate = useAppSelector(selectSalesComparisonApproach) === 'unit_rate';

  const qualityField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.quality,
    manualAdjustmentKey: 'quality',
    toDisplayValue: (improvements?: Improvements) => {
      const { craftsmanQuality, propertyType, uadQuality } = improvements ?? {};

      let foundationLabel: ManufacturedHomeFoundationDisplay | undefined;
      if (propertyType === 'manufacturedHome') {
        const { foundation } = improvements as ManufacturedHomeFeatures;
        foundationLabel = getFoundationDisplay(foundation);
      }

      const foundationDisplayLabel = foundationLabel ? ` (${foundationLabel})` : '';
      return `Q${uadQuality ?? '-'}/${craftsmanQuality ?? '-'}${foundationDisplayLabel}`;
    },
  };

  const conditionField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.condition,
    manualAdjustmentKey: 'condition',
    toDisplayValue: (improvements) => {
      const { effectiveAge, uadCondition } = improvements ?? {};
      return `C${uadCondition ?? '-'}/E${effectiveAge ?? '-'}`;
    },
  };

  const halfBathCountField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.halfBaths,
    manualAdjustmentKey: 'halfBaths',
    unitRate: {
      key: 'halfBaths',
      label: '/ba',
    },
    toDisplayValue: (improvements) =>
      isDefined(improvements?.halfBaths) ? `${improvements.halfBaths ?? 0}` : undefined,
  };

  const fullBathCountField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.fullBaths,
    manualAdjustmentKey: 'fullBaths',
    unitRate: {
      key: 'fullBaths',
      label: '/ba',
    },
    toDisplayValue: (improvements) =>
      isDefined(improvements?.fullBaths) ? `${improvements.fullBaths ?? 0}` : undefined,
  };

  const bathAboveGradeField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.bathAboveGrade,
    manualAdjustmentKey: 'bathAboveGrade',
    toDisplayValue: (improvements) => {
      if (!isDefined(improvements?.fullBaths) && !isDefined(improvements?.halfBaths))
        return undefined;
      return `${improvements?.fullBaths ?? 0}.${improvements?.halfBaths ?? 0}`;
    },
  };

  const depreciationFields: ImprovementField[] = [];

  if (orderHasFunctionalDepreciation) {
    depreciationFields.push({
      fieldName: improvementsFieldName,
      label: ADJUSTMENT_LABELS.functionalUtility,
      manualAdjustmentKey: 'functionalUtility',
      toDisplayValue: () => undefined,
    });
  }

  if (orderHasExternalDepreciation) {
    depreciationFields.push({
      fieldName: improvementsFieldName,
      label: ADJUSTMENT_LABELS.externalObsolescence,
      manualAdjustmentKey: 'externalObsolescence',
      toDisplayValue: () => undefined,
    });
  }

  return (
    <PropertyGridSection
      {...props}
      title="Main Dwelling"
      hasSeparateArvValue
      fields={[
        ...(isUnitRate
          ? [
              getNumberPropertyGridField({
                fieldName: 'editableData.improvements.age',
                label: ADJUSTMENT_LABELS.actualAge,
                manualAdjustmentKey: 'actualAge',
                unitRate: {
                  key: 'actualAge',
                  label: '/yr',
                },
              }),
            ]
          : []),
        qualityField,
        conditionField,
        getAloftAreaJSONPropertyGridField({
          fieldName: 'editableData.improvements.gla',
          label: 'GLA',
          manualAdjustmentKey: 'gla',
          unitRate: {
            key: 'mainDwelling',
            label: '/sf',
          },
        }),
        getNumberPropertyGridField({
          fieldName: 'editableData.improvements.bedrooms',
          label: 'Bed Count',
          manualAdjustmentKey: 'bedrooms',
          unitRate: {
            key: 'bedrooms',
            label: '/bed',
          },
        }),
        ...(isUnitRate ? [fullBathCountField, halfBathCountField] : [bathAboveGradeField]),
        ...depreciationFields,
      ]}
    />
  );
};
